// src/App.js
import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import EventsPage from './pages/EventsPage.js';
import FamilyTreePage from './pages/FamilyTreePage.js';
import FamilyBoardDashboard from './pages/FamilyBoard/FamilyBoardDashboard.js'; // Comprehensive dashboard with widgets
import UserHome from './pages/UserHome.js';
import AnnouncementsPage from './pages/AnnouncementsPage.js';
import DiscussionsPage from './pages/DiscussionsPage.js';
import HealthUpdatesPage from './pages/HealthUpdatesPage.js';
import RecommendationsPage from './pages/RecommendationsPage.js';
import GoalsPage from './pages/GoalsPage.js';
import EmergencyInfoPage from './pages/EmergencyInfoPage.js';
import FunSocialPage from './pages/FunSocialPage.js';
import TestPage from './pages/TestPage.js';
import DocumentsTestPage from './pages/DocumentsTestPage.js';
import PhotosPage from './pages/PhotosPage.js';
import Contacts from './pages/Contacts/Contacts.js';
import RecipesPage from './pages/Recipes/RecipesPage.js';
import BirthdayCalendarPage from './pages/BirthdayCalendarPage.js';
import FamilyDirectoryPage from './pages/FamilyDirectoryPage.js';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FamilySessionProvider } from './hooks/useFamilySession';
import Login from './Components/Login';
import Home from './pages/home/Home'; // Enhanced Activity Dashboard
import Profile from './Components/Profile';
import Documents from './Components/Documents';
import AdminPortal from './Components/AdminPortal/AdminPortal';
import AccountSettings from './Components/AccountSettings';
import SubscriptionManager from './Components/SubscriptionManager';
import Layout from './Components/Layout';
import AccountCreationWizard from './Components/AccountCreationWizard';
import InviteAcceptPage from './Components/InviteAcceptPage';
import TrialUpgradeGate from './Components/TrialUpgradeGate';
import SystemAdminLayout from './Components/SystemAdmin/SystemAdminLayout';
import SystemAdminDashboard from './Components/SystemAdmin/SystemAdminDashboard';
import SystemNotifications from './Components/SystemAdmin/SystemNotifications';
import SystemUserManagement from './Components/SystemAdmin/SystemUserManagement';
import HelpDeskManagement from './Components/SystemAdmin/HelpDeskManagement';
import SystemAnalytics from './Components/SystemAdmin/SystemAnalytics';
import SystemAuditLogs from './Components/SystemAdmin/SystemAuditLogs';
import SystemSettings from './Components/SystemAdmin/SystemSettings';
import SystemAdminInviteManager from './Components/SystemAdmin/SystemAdminInviteManager';
import SystemAdminOnboardingWizard from './Components/SystemAdmin/SystemAdminOnboardingWizard';
import { isInviteOnlyMode } from './utils/inviteOnlyMode';
import { userDocHasFamilyContext } from './utils/familyScope';
import HelpCenterPage from './pages/HelpCenter/HelpCenterPage';
import TutorialsPage from './pages/Tutorials/TutorialsPage';
import AboutPage from './pages/About/AboutPage';
import MessagesPage from './pages/Messages/MessagesPage';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import TermsOfService from './pages/Legal/TermsOfService';
import CookiePolicy from './pages/Legal/CookiePolicy';
import CookieConsentBanner from './Components/CookieConsentBanner';
import DevBanner from './Components/DevBanner';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db, firebasePromise } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { isSystemAdmin } from './utils/systemPermissions';
import logger from './utils/logger';
import { setupNotifications } from './frontend/NotificationHandler';
import EmergencySOSButton from './Components/EmergencySOSButton';
import PushNotificationToast from './Components/PushNotificationToast';
import PWAInstallBanner from './Components/PWAInstallBanner';

// Loading component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

// Protected Route component
function ProtectedRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" />;
}

// Admin Route component (for family admin portal)
function AdminRoute({ children }) {
  const { currentUser, loading } = useAuth();
  const [userGroups, setUserGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);

  // Fetch user's groups from nested member document
  useEffect(() => {
    const fetchUserGroups = async () => {
      if (!currentUser?.uid || !currentUser?.familyCode) {
        setUserGroups([]);
        setGroupsLoading(false);
        return;
      }

      try {
        const memberRef = doc(db, 'families', currentUser.familyCode, 'members', currentUser.uid);
        const memberDoc = await getDoc(memberRef);

        if (memberDoc.exists()) {
          const memberData = memberDoc.data();
          setUserGroups(memberData.groups || []);
        } else {
          setUserGroups([]);
        }
      } catch (error) {
        logger.error('Error fetching user groups for admin check:', error);
        setUserGroups([]);
      } finally {
        setGroupsLoading(false);
      }
    };

    if (!loading) {
      fetchUserGroups();
    }
  }, [currentUser?.uid, currentUser?.familyCode, loading]);

  if (loading || groupsLoading) {
    return <LoadingFallback />;
  }

  // Check for ADMIN group membership or legacy admin flags
  const isAdmin = userGroups.includes('ADMIN') ||
                  currentUser?.isAdmin ||
                  currentUser?.isSuperAdmin;

  if (!currentUser || !isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
}

// System Admin Route component (for system admin portal)
function SystemAdminRoute({ children }) {
  const { currentUser, loading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Fetch user profile to check if system admin
  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser?.uid) {
        setProfile(null);
        setProfileLoading(false);
        return;
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          setProfile(userDoc.data());
        } else {
          setProfile(null);
        }
      } catch (error) {
        logger.error('Error fetching user profile for system admin check:', error);
        setProfile(null);
      } finally {
        setProfileLoading(false);
      }
    };

    if (!loading) {
      fetchProfile();
    }
  }, [currentUser?.uid, loading]);

  if (loading || profileLoading) {
    return <LoadingFallback />;
  }

  // Check if user is system admin
  if (!currentUser || !isSystemAdmin(profile)) {
    return <Navigate to="/" />;
  }

  return children;
}

// Route guard: redirects users who haven't completed signup to the wizard,
// and redirects system_admin accounts to the system admin dashboard
function FamilyPortalGuard({ children }) {
  const { currentUser, loading } = useAuth();
  const [accountType, setAccountType] = useState(null);
  const [hasProfile, setHasProfile] = useState(null);
  const [checkingType, setCheckingType] = useState(true);

  useEffect(() => {
    const checkAccountType = async () => {
      if (!currentUser?.uid) {
        setCheckingType(false);
        return;
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          setAccountType(data.accountType || 'family');
          // Check if the user has completed signup (has a familyCode or currentFamily)
          setHasProfile(userDocHasFamilyContext(data));
        } else {
          // No Firestore profile at all — user hasn't gone through signup
          setAccountType('family');
          setHasProfile(false);
        }
      } catch {
        setAccountType('family');
        setHasProfile(false);
      } finally {
        setCheckingType(false);
      }
    };

    if (!loading) {
      checkAccountType();
    }
  }, [currentUser?.uid, loading]);

  if (loading || checkingType) {
    return <LoadingFallback />;
  }

  // System admin accounts without a family should go to system admin dashboard
  if (accountType === 'system_admin' && !currentUser?.familyCode) {
    return <Navigate to="/system-admin/dashboard" replace />;
  }

  // Users who haven't completed signup (no familyCode/profile) must go through the wizard
  if (!hasProfile) {
    return <Navigate to="/signup" replace />;
  }

  return children;
}

// AppContent component to use the AuthContext
function AppContent() {
  const { currentUser, logout } = useAuth();

  // Set up FCM push notifications once the user is authenticated
  useEffect(() => {
    if (currentUser?.uid) {
      setupNotifications(currentUser.uid).catch(() => {});
    }
  }, [currentUser?.uid]);

  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <DevBanner />
        <CookieConsentBanner />
        {/* PWA "Add to Home Screen" install banner — fires on beforeinstallprompt */}
        <PWAInstallBanner />
        {/* Foreground push notification toasts — inside Router so useNavigate works */}
        {currentUser && <PushNotificationToast />}
        {/* Emergency SOS floating button — visible to all authenticated users with a family */}
        {currentUser && <EmergencySOSButton />}
        <Routes>
          {/* Login route: existing users with a profile go to home.
              Authenticated users without a profile are NOT auto-redirected
              to /signup here — Login.js handles SSO routing explicitly to
              avoid the glitch where the wizard mounts before Firestore data
              loads. FamilyPortalGuard on "/" handles the redirect for users
              who navigate directly. */}
          <Route path="/login" element={
            currentUser?.isSuperAdmin
              ? <Navigate to="/system-admin/dashboard" replace />
              : currentUser?.activeFamilyCode || currentUser?.familyCode || currentUser?.currentFamily
              ? <Navigate to="/" />
              : <Login />
          } />
          <Route path="/register" element={<Navigate to="/signup" />} />
          <Route path="/signup" element={
            isInviteOnlyMode()
              ? <Navigate to="/login" />
              : (!currentUser?.familyCode && !currentUser?.currentFamily
                  ? <AccountCreationWizard />
                  : <Navigate to="/" />)
          } />
          <Route path="/join/:familyCode/:inviteCode" element={
            !currentUser
              ? <AccountCreationWizard />
              : (!currentUser?.familyCode && !currentUser?.currentFamily
                  ? <AccountCreationWizard />
                  : <InviteAcceptPage />)
          } />

          {/* System Admin Onboarding Route (public - no auth required) */}
          <Route path="/system-join/:inviteCode" element={!currentUser ? <SystemAdminOnboardingWizard /> : <Navigate to="/system-admin/dashboard" />} />

          {/* Public Legal Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-and-conditions" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />

          {/* System Admin Portal Routes */}
          <Route
            path="/system-admin"
            element={
              <ProtectedRoute>
                <SystemAdminRoute>
                  <SystemAdminLayout />
                </SystemAdminRoute>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/system-admin/dashboard" replace />} />
            <Route path="dashboard" element={<SystemAdminDashboard />} />
            <Route path="notifications" element={<SystemNotifications />} />
            <Route path="users" element={<SystemUserManagement />} />
            {/* System Admin Routes */}
            <Route path="analytics" element={<SystemAnalytics />} />
            <Route path="audit-logs" element={<SystemAuditLogs />} />
            <Route path="support" element={<HelpDeskManagement />} />
            <Route path="settings" element={<SystemSettings />} />
            <Route path="invitations" element={<SystemAdminInviteManager />} />
          </Route>

          {/* Family Portal Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <FamilyPortalGuard>
                  <Layout onSignOut={logout} />
                </FamilyPortalGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<Home />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<AccountSettings />} />
            <Route path="subscription" element={<SubscriptionManager />} />
            <Route path="documents" element={<TrialUpgradeGate><DocumentsTestPage /></TrialUpgradeGate>} />
            <Route path="photos" element={<TrialUpgradeGate><PhotosPage /></TrialUpgradeGate>} />
            <Route path="contacts" element={<TrialUpgradeGate><Contacts /></TrialUpgradeGate>} />
            {/* Groups route removed - group management is now only in Admin Portal */}
            <Route path="events" element={<EventsPage />} />
            <Route path="family-tree" element={<FamilyTreePage />} />
            <Route path="family-board" element={<FamilyBoardDashboard />} />
            <Route path="user-home" element={<UserHome />} />
            <Route path="announcements" element={<AnnouncementsPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="messages/:conversationId" element={<MessagesPage />} />
            <Route path="discussions" element={<DiscussionsPage />} />
            <Route path="health-updates" element={<TrialUpgradeGate><HealthUpdatesPage /></TrialUpgradeGate>} />
            <Route path="recommendations" element={<TrialUpgradeGate><RecommendationsPage /></TrialUpgradeGate>} />
            <Route path="recipes" element={<TrialUpgradeGate><RecipesPage /></TrialUpgradeGate>} />
            <Route path="goals" element={<TrialUpgradeGate><GoalsPage /></TrialUpgradeGate>} />
            <Route path="birthdays" element={<BirthdayCalendarPage />} />
            <Route path="family-directory" element={<TrialUpgradeGate><FamilyDirectoryPage /></TrialUpgradeGate>} />
            <Route path="help-center" element={<HelpCenterPage />} />
            <Route path="tutorials" element={<TutorialsPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="emergency-info" element={<EmergencyInfoPage />} />
            <Route path="fun-social" element={<FunSocialPage />} />
            <Route path="test" element={<TestPage />} />
            <Route path="documents-old" element={<Documents />} />
            <Route path="admin" element={<AdminRoute><AdminPortal /></AdminRoute>} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
}

function App() {
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);
  const [initError, setInitError] = useState(null);
  const [, loading, error] = useAuthState(auth);

  useEffect(() => {
    const initializeFirebase = async () => {
      try {
        await firebasePromise;
        setFirebaseInitialized(true);
      } catch (err) {
        logger.error('Error during Firebase initialization:', err);
        setInitError(err.message);
      }
    };

    initializeFirebase();
  }, []);

  if (initError) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" color="error" gutterBottom>
          Failed to initialize application
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {initError}
        </Typography>
      </Box>
    );
  }

  if (!firebaseInitialized || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
        <Typography variant="body1" ml={2}>
          {!firebaseInitialized ? 'Initializing application...' : 'Loading...'}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" color="error" gutterBottom>
          Authentication Error
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <ProfileProvider>
          <FamilySessionProvider>
            <AppContent />
          </FamilySessionProvider>
        </ProfileProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
